import { DEFAULT_CURRENCY_CODE } from '@/modules/common';
import { Department } from '@/modules/settings/types/department';
import { Role } from '@/modules/settings/types/role';
import { Seniority } from '@/modules/settings/types/seniority';
import { TeamMemberFormData } from '@/modules/team/types';
import { CreateTeamMemberRequest } from '@/modules/team/types/dto/request/create-team-member-request';
import { UpdateTeamMemberBillingRequest } from '@/modules/team/types/dto/request/update-team-member-billing-request';
import { UpdateTeamMemberCapacityRequest } from '@/modules/team/types/dto/request/update-team-member-capacity-request';
import { UpdateTeamMemberInfoRequest } from '@/modules/team/types/dto/request/update-team-member-info-request';
import { TeamMemberTypeDTO } from '@/modules/team/types/dto/team-member-type.dto';
import { MembershipType } from '@/modules/team/types/membership-type';
import { InternalRateType, TeamMemberType } from '@/modules/team/types/team-member';
import { isValidMembershipType } from '@/modules/team/utils/membership-type.utils';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

/**
 * The backend rejects a null/empty membershipType (OPS-1472). Coerce any empty or
 * invalid value to the default so every create/update request always carries a valid one.
 */
const resolveMembershipType = (value: unknown): MembershipType =>
  isValidMembershipType(value) ? value : MembershipType.InHouse;

/**
 * Maps domain TeamMemberType to DTO for API requests
 */
const mapTeamMemberTypeToDTO = (type?: TeamMemberType): TeamMemberTypeDTO => {
  switch (type) {
    case TeamMemberType.Placeholder:
      return TeamMemberTypeDTO.PLACEHOLDER;
    case TeamMemberType.TeamMember:
    default:
      return TeamMemberTypeDTO.REGULAR_MEMBER;
  }
};

/**
 * Extracts ID from entity that can be an object, number, or string ID
 * Handles Department, Role, Seniority types
 */
const extractEntityId = (
  entity: Department | Role | Seniority | number | string | undefined
): number | null => {
  if (!entity) return null;
  if (typeof entity === 'number') return entity;
  if (typeof entity === 'string') return parseInt(entity, 10) || null;
  if (typeof entity === 'object' && 'id' in entity) return entity.id;
  return null;
};

/**
 * Maps TeamMemberFormData to CreateTeamMemberRequest
 * Converts domain model to API request format
 */
export const mapTeamMemberFormDataToCreateRequest = (
  data: TeamMemberFormData
): CreateTeamMemberRequest => {
  // Convert InternalRateType to boolean
  const isHourlyRated = data.internalRateType === InternalRateType.HourlyCostRate;

  // Determine if placeholder
  const isPlaceholder = data.type === 'placeholder';

  // Convert managerId string to number or null
  const managerId = data.managerId ? parseInt(data.managerId, 10) : null;

  // Handle capacity fields based on onDemand status
  const isOnDemand = !!data.isOnDemandAvailability;
  const totalWorkingMinutes = isOnDemand ? 0 : data.capacityPerDay! * 60;
  // Round to 5 decimal places to match backend precision requirements
  const rawBillableCapacity = isOnDemand ? 100 : data.billableCapacity || 0;
  const billableCapacity = parseFloat(rawBillableCapacity.toFixed(5));

  return {
    name: data.name,
    email: data.email || null,
    isPlaceholder,
    phoneNumber: data.phoneNumber || '',
    country: data.country || '',
    city: data.city || '',
    about: data.about || '',
    departmentId: extractEntityId(data.department),
    roleId: extractEntityId(data.role),
    seniorityId: extractEntityId(data.seniority),
    managerId,
    isManager: data.isManager,
    tags: data.tags,
    membershipType: resolveMembershipType(data.membershipType),
    isHourlyRated: isHourlyRated ?? false,
    supportedCurrencyId: data.internalRateCurrency ?? DEFAULT_CURRENCY_CODE,
    internalRate: data.internalRate ?? 0,
    externalRate: data.externalHourlyRate ?? 0,
    onDemand: isOnDemand,
    totalWorkingMinutes,
    billableCapacity,
    dateOfJoin: data.dateOfJoin
      ? normalizeToStartOfDayUTC(new Date(data.dateOfJoin)).toISOString()
      : undefined,
    customAttributesValues: data.customAttributesValues,
  };
};

/**
 * Maps TeamMemberFormData to UpdateTeamMemberInfoRequest
 * Includes effectiveStartDate and optional effectiveEndDate
 */
export const mapTeamMemberFormDataToUpdateInfoRequest = (
  data: Partial<TeamMemberFormData>,
  memberId: number,
  effectiveStartDate: Date,
  effectiveEndDate?: Date
): UpdateTeamMemberInfoRequest => {
  // Convert managerId string to number or null
  const managerId = data.managerId ? parseInt(data.managerId, 10) : null;
  const isPlaceholder = data.type === TeamMemberType.Placeholder;

  return {
    teamMemberId: memberId,
    name: data.name || '',
    // Placeholder members don't have contact/location fields — send null/empty to clear them
    email: isPlaceholder ? null : data.email || null,
    phoneNumber: isPlaceholder ? '' : data.phoneNumber || '',
    country: isPlaceholder ? '' : data.country || '',
    city: isPlaceholder ? '' : data.city || '',
    about: isPlaceholder ? '' : data.about || '',
    departmentId: extractEntityId(data.department),
    roleId: extractEntityId(data.role),
    seniorityId: extractEntityId(data.seniority),
    managerId,
    isManager: data.isManager ?? false,
    tags: data.tags,
    membershipType: resolveMembershipType(data.membershipType),
    type: mapTeamMemberTypeToDTO(data.type),
    startDate: normalizeToStartOfDayUTC(effectiveStartDate).toISOString(),
    endDate: effectiveEndDate
      ? normalizeToStartOfDayUTC(effectiveEndDate).toISOString()
      : undefined,
    customAttributesValues: data.customAttributesValues,
  };
};

/**
 * Maps TeamMemberFormData to UpdateTeamMemberBillingRequest
 * Converts InternalRateType to boolean
 */
export const mapTeamMemberFormDataToUpdateBillingRequest = (
  data: Partial<TeamMemberFormData>,
  memberId: number,
  effectiveStartDate: Date,
  effectiveEndDate?: Date
): UpdateTeamMemberBillingRequest => {
  // Convert InternalRateType to boolean
  const isHourlyRated = data.internalRateType === InternalRateType.HourlyCostRate;

  return {
    teamMemberId: memberId,
    isHourlyRated: data.internalRateType ? isHourlyRated : undefined,
    supportedCurrencyId: data.internalRateCurrency ?? undefined,
    internalRate: data.internalRate ?? undefined,
    externalRate: data.externalHourlyRate ?? undefined,
    startDate: normalizeToStartOfDayUTC(effectiveStartDate).toISOString(),
    endDate: effectiveEndDate
      ? normalizeToStartOfDayUTC(effectiveEndDate).toISOString()
      : undefined,
  };
};

/**
 * Maps TeamMemberFormData to UpdateTeamMemberCapacityRequest
 * Converts capacityPerDay (hours) to totalWorkingMinutes (minutes)
 * When onDemand is true, sets default values for capacity fields
 */
export const mapTeamMemberFormDataToUpdateCapacityRequest = (
  data: Partial<TeamMemberFormData>,
  memberId: number,
  effectiveStartDate: Date,
  effectiveEndDate?: Date
): UpdateTeamMemberCapacityRequest => {
  const isOnDemand = data.isOnDemandAvailability ?? false;

  // When onDemand is true, set default values for hidden fields
  const totalWorkingMinutes = isOnDemand ? 0 : (data.capacityPerDay || 0) * 60;
  // Round to 5 decimal places to match backend precision requirements
  const rawBillableCapacity = isOnDemand ? 100 : data.billableCapacity || 0;
  const billableCapacity = parseFloat(rawBillableCapacity.toFixed(5));

  return {
    teamMemberId: memberId,
    onDemand: isOnDemand,
    totalWorkingMinutes,
    billableCapacity,
    startDate: normalizeToStartOfDayUTC(effectiveStartDate).toISOString(),
    endDate: effectiveEndDate
      ? normalizeToStartOfDayUTC(effectiveEndDate).toISOString()
      : undefined,
  };
};
